import { hexToRgb } from "common/utils/colorConversion";
import * as THREE from "three";
import { designMaterialAsFlatColor } from "./designMaterialAsFlatColor";
import {
  designMaterialLayersAreFlatColor,
  normalizeSdfPatternRotation,
  sdfPeriod,
  SdfPeriodPurpose,
  usesSdf,
} from "./designMaterialParams";
import type { DesignMaterialParamsPreprocessed, LayerParamsPreprocessed } from "./designMaterialTypes";
import type { BakedColorTexture } from "./designMaterialTypes";

export function designMaterialBakesToFlatColor(params: DesignMaterialParamsPreprocessed) {
  return designMaterialLayersAreFlatColor(params) && !usesSdf(params);
}

export function designMaterialColorVariantsCanUseTemplateTexture(templateParams: DesignMaterialParamsPreprocessed) {
  function isGreyscaleColor(color: string) {
    const rgb = hexToRgb(color);
    return rgb[0] === rgb[1] && rgb[1] === rgb[2];
  }

  function isDarkGreyscaleColor(color: string) {
    const rgb = hexToRgb(color);
    return rgb[0] === rgb[1] && rgb[1] === rgb[2] && rgb[0] < 128;
  }

  function isLowIntensityGreyscaleLayer(layer: LayerParamsPreprocessed) {
    const colors = [layer.color0, layer.color1];
    if (layer.colorCount > 2) {
      colors.push(layer.color2);
    }
    if (layer.colorCount > 3) {
      colors.push(layer.color3);
    }
    const colorStrength = layer.strength * layer.colorInfluence;
    if (colors.every((color) => isGreyscaleColor(color)) && colorStrength < 0.06) {
      return true;
    }
    if (colors.every((color) => isDarkGreyscaleColor(color)) && colorStrength < 0.15 && layer.blendMode === "alpha") {
      return true;
    }
    return false;
  }

  if (
    templateParams.layers.every(
      (layer) =>
        layer.blendMode === "multiply" || // Multiply layers will apply the same way whether they're applied before the color or after the color is applied.
        layer.strength * layer.colorInfluence < 0.01 ||
        isLowIntensityGreyscaleLayer(layer),
    )
  ) {
    return true;
  }
  return false;
}

const whiteTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
whiteTexture.needsUpdate = true;
export function flatColorBakedMaterial(params: DesignMaterialParamsPreprocessed): BakedColorTexture {
  const color = designMaterialAsFlatColor(params);
  return {
    // We use a placeholder texture even though we don't really need it so that the shader is the same as for the textured variant.
    texture: whiteTexture,
    color: new THREE.Color(...color),
    worldWidth: 10,
    worldHeight: 10,
    rotation: 0,
    loadedPromise: Promise.resolve(),
  };
}

export function designMaterialBakeRepeatPeriodAndRotation(params: DesignMaterialParamsPreprocessed): {
  rotation: number;
  repeatPeriodX: number;
  repeatPeriodY: number;
  mainMaskTileRepeatInterval: number;
} {
  const { normalizedPatterns, rotation: sdfRotation = 0 } = normalizeSdfPatternRotation(
    params.sdf.common,
    params.sdf.patterns,
  );

  let rotation = sdfRotation;
  let repeatPeriodX = 200;
  let repeatPeriodY = 200;
  let mainMaskTileRepeatInterval = 1;

  if (params.heightMapEnabled && params.heightMapName) {
    repeatPeriodX = params.heightMapScaleU;
    repeatPeriodY = params.heightMapScaleV;
    rotation = params.heightMapRotation;
  } else if (normalizedPatterns.length > 0) {
    const { repeatPeriodX: sdfRepeatPeriodX, repeatPeriodY: sdfRepeatPeriodY } = sdfPeriod(
      params.sdf.common,
      normalizedPatterns,
      SdfPeriodPurpose.HeightMapPeriod, // We'll use the same repeat period as we use for the heightmap, it should be suitable for layers as well.
    );
    if (sdfRepeatPeriodX && sdfRepeatPeriodY) {
      mainMaskTileRepeatInterval = 2;
      repeatPeriodX = sdfRepeatPeriodX * mainMaskTileRepeatInterval;
      repeatPeriodY = sdfRepeatPeriodY * mainMaskTileRepeatInterval;
    }
  }

  return { rotation, repeatPeriodX, repeatPeriodY, mainMaskTileRepeatInterval };
}
